import { List, useListContext, useRedirect, CreateButton, TopToolbar } from 'react-admin';
import {
  Box, Typography, Stack, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TextField, InputAdornment, MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useEffect, useMemo } from 'react';
import { tbl, btn, ORDER_STATUS, ORDER_PAYMENT_STATUS, ORDER_ORIGIN, badgeStyle, inputSx, clickableA11y } from '../styles/ds';
import { useFitCount } from '../hooks/useFitCount';
import FitPagination from '../components/FitPagination';

const fmtIls  = v => `₪${Number(v || 0).toLocaleString('he-IL', { minimumFractionDigits: 0 })}`;
const fmtUsd  = v => `$${Number(v || 0).toLocaleString('en-US',  { minimumFractionDigits: 0 })}`;
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const Toolbar = () => (
  <TopToolbar>
    <CreateButton label="הזמנה חדשה" sx={btn.create} />
  </TopToolbar>
);

const OrderTable = () => {
  const { data = [], isLoading, setFilters } = useListContext();
  const redirect = useRedirect();
  const [search, setSearch] = useState('');
  const [origin, setOrigin] = useState('');
  const [page, setPage] = useState(1);
  const { fitCount, renderCount, containerRef, startIndex } = useFitCount({ fallback: 10, page });

  // Send the search term to the backend so ALL orders are searched, not just
  // the current page. Without this, only the ~10 loaded rows were searched
  // and old completed orders (sorted to the bottom) were never found.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters({
        ...(search.trim() ? { q: search.trim() } : {}),
        ...(origin ? { origin } : {}),
      });
    }, 350);
    return () => clearTimeout(t);
  }, [search, origin]); // eslint-disable-line react-hooks/exhaustive-deps

  // A new server-side search result is a different set — always land back
  // on page 1 rather than a now-meaningless page number.
  useEffect(() => { setPage(1); }, [data]);

  const pageItems = useMemo(() => {
    return data.slice(startIndex, startIndex + renderCount);
  }, [data, startIndex, renderCount]);

  if (isLoading) return null;

  return (
    <Box>
      <Box className="crm-list-search" px={1} py={1.5}>
        <TextField
          size="small"
          placeholder="חיפוש לפי שם לקוח או מספר הזמנה..." aria-label="חיפוש הזמנות"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
            sx: { ...inputSx, direction: 'rtl' },
          }}
          sx={{ width: '100%', maxWidth: 400 }}
        />
        {/* Website Order / CRM Order filter (docs/website/ WS-9.1) */}
        <TextField
          select size="small" value={origin} onChange={e => setOrigin(e.target.value)}
          aria-label="סינון לפי מקור ההזמנה"
          SelectProps={{ displayEmpty: true }}
          InputProps={{ sx: { ...inputSx, direction: 'rtl' } }}
          sx={{ width: 160, ml: 1.5 }}
        >
          <MenuItem value="">כל המקורות</MenuItem>
          {Object.entries(ORDER_ORIGIN).map(([id, m]) => (
            <MenuItem key={id} value={id}>{`הזמנות ${m.label}`}</MenuItem>
          ))}
        </TextField>
      </Box>

      <TableContainer component={Box} sx={{ ...tbl.container, border: '1px solid rgba(13,29,62,0.06)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}>#</TableCell>
              <TableCell sx={{ width: 90 }} align="center">מספר הזמנה</TableCell>
              <TableCell sx={{ minWidth: 160 }}>לקוח</TableCell>
              <TableCell align="center">מקור</TableCell>
              <TableCell align="center">סטטוס הזמנה</TableCell>
              <TableCell align="center">סטטוס תשלום</TableCell>
              <TableCell align="right">סה"כ ₪</TableCell>
              <TableCell align="right">סה"כ $</TableCell>
              <TableCell align="center">תאריך</TableCell>
            </TableRow>
          </TableHead>
          <TableBody ref={containerRef}>
            {pageItems.map((order, i) => {
              const name    = `${order.first_name ?? ''} ${order.last_name ?? ''}`.trim() || '—';
              const initials = name !== '—'
                ? `${(order.first_name ?? '')[0] ?? ''}${(order.last_name ?? '')[0] ?? ''}`.toUpperCase()
                : '?';
              return (
                <TableRow key={order.id} onClick={() => redirect('edit', 'orders', order.id)}
                  {...clickableA11y(() => redirect('edit', 'orders', order.id), { ariaLabel: `פתיחת הזמנה מספר ${order.id}` })}
                  sx={tbl.bodyRow(i)}>
                  <TableCell>
                    <Typography fontWeight={600} color="text.secondary" fontSize={13}>#{order.id}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={700} fontSize={13} color="primary.main">
                      {order.customer_order_number ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.75} alignItems="center">
                      <Avatar sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700,
                        backgroundColor: 'rgba(13,29,62,0.12)', color: '#0d1d3e' }}>
                        {initials}
                      </Avatar>
                      <Typography fontWeight={600} fontSize={14}>{name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    {order.origin
                      ? <span style={badgeStyle(ORDER_ORIGIN, order.origin)}>{ORDER_ORIGIN[order.origin]?.label || order.origin}</span>
                      : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <span style={badgeStyle(ORDER_STATUS, order.status)}>
                      {ORDER_STATUS[order.status]?.label || order.status}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <span style={badgeStyle(ORDER_PAYMENT_STATUS, order.payment_status || 'unpaid')}>
                      {(ORDER_PAYMENT_STATUS[order.payment_status] || ORDER_PAYMENT_STATUS.unpaid).label}
                    </span>
                  </TableCell>
                  {/* A website order has no final amount until the admin approves it
                      (total NULL), and it is priced in USD only. */}
                  <TableCell align="right">
                    {order.origin === 'website'
                      ? <Typography fontSize={13} color="text.secondary">—</Typography>
                      : <Typography fontWeight={700} color="primary.main" fontSize={14}>{fmtIls(order.total_price_ils)}</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    {order.origin === 'website' && order.total_price_usd === null
                      ? <Typography fontSize={13} color="warning.main">ממתין לאישור</Typography>
                      : <Typography fontWeight={600} color="text.secondary" fontSize={13}>{fmtUsd(order.total_price_usd)}</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontSize={13} color="text.secondary">{fmtDate(order.created_at)}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  {search || origin ? 'לא נמצאו תוצאות' : 'אין הזמנות עדיין'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <FitPagination page={page} setPage={setPage} total={data.length} perPage={fitCount} />
    </Box>
  );
};

const OrderList = () => (
  <List className="crm-mobile-list crm-inline-list"
    sort={{ field: 'id', order: 'DESC' }}
    perPage={1000}
    pagination={false}
    storeKey={false}
    disableSyncWithLocation
    actions={<Toolbar />}
    title="הזמנות"
    sx={tbl.listSx}
  >
    <OrderTable />
  </List>
);

export default OrderList;
